import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { colors, baseStyles } from '../styles';
import { ActionButtons, FavoriteButton, FeedbackButtons } from '../components';
import { getPlace } from '../services/placesService';

// Formatear texto de estacionamiento
const formatearEstacionamiento = (estacionamiento) => {
  if (!estacionamiento || typeof estacionamiento !== 'object' || !estacionamiento.tipo) {
    // Si es string antiguo, retornarlo tal cual
    if (typeof estacionamiento === 'string') return estacionamiento;
    return null;
  }

  const tipoTextos = {
    propio: 'Estacionamiento propio',
    calle: 'En la calle',
    plaza: 'Plaza cercana',
    sin: 'Sin estacionamiento'
  };

  const facilidadTextos = {
    muy_facil: 'Muy facil',
    facil: 'Facil',
    medio: 'Medio',
    dificil: 'Preferible transporte publico'
  };

  let texto = tipoTextos[estacionamiento.tipo] || '';

  if (estacionamiento.tipo === 'propio' && estacionamiento.lugares) {
    texto += ` (${estacionamiento.lugares} lugares)`;
  }

  if (estacionamiento.facilidad) {
    texto += ` · ${facilidadTextos[estacionamiento.facilidad]}`;
  }

  return texto;
};

const styles = {
  ...baseStyles,
  photoContainer: {
    width: '100%',
    height: '200px',
    backgroundColor: colors.surfaceHover,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
    overflow: 'hidden',
  },
  photoPlaceholder: {
    color: colors.textMuted,
    fontSize: '3rem',
  },
  nombreRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginBottom: '0.25rem',
  },
  nombre: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: colors.textPrimary,
    margin: 0,
    flex: 1,
  },
  sourceBadge: {
    fontSize: '0.7rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  sourceGoogle: {
    backgroundColor: '#e8f0fe',
    color: '#1967d2',
  },
  sourceVerified: {
    backgroundColor: colors.successBg || '#d1fae5',
    color: colors.success || '#059669',
  },
  ratingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    marginBottom: '0.25rem',
  },
  star: {
    color: '#fbbf24',
  },
  rating: {
    fontSize: '0.875rem',
    color: colors.textPrimary,
    fontWeight: 500,
  },
  resenas: {
    fontSize: '0.875rem',
    color: colors.textSecondary,
  },
  categoria: {
    fontSize: '0.875rem',
    color: colors.textSecondary,
    marginTop: '0.25rem',
  },
  infoSection: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  },
  estadoContainer: {
    marginBottom: '0.75rem',
  },
  estadoAbierto: {
    fontSize: '0.875rem',
    color: colors.success,
    fontWeight: 500,
  },
  estadoCerrado: {
    fontSize: '0.875rem',
    color: colors.error,
    fontWeight: 500,
  },
  estado24h: {
    fontSize: '0.75rem',
    color: colors.success,
    fontWeight: 500,
    backgroundColor: colors.successBg,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    display: 'inline-block',
  },
  direccion: {
    fontSize: '0.875rem',
    color: colors.textPrimary,
    marginBottom: '0.25rem',
  },
  ciudad: {
    fontSize: '0.8rem',
    color: colors.textSecondary,
  },
  zonas: {
    fontSize: '0.875rem',
    color: colors.textPrimary,
    marginBottom: '0.25rem',
  },
  horario: {
    fontSize: '0.875rem',
    color: colors.textSecondary,
    marginBottom: '0.75rem',
  },
  actionsSection: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  },
  detailsSection: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  },
  detailsTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '1rem',
  },
  detailRow: {
    marginBottom: '1rem',
  },
  detailLabel: {
    fontSize: '0.8rem',
    color: colors.textSecondary,
    marginBottom: '0.25rem',
  },
  detailValue: {
    fontSize: '0.875rem',
    color: colors.textPrimary,
  },
  serviciosList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.375rem',
  },
  servicioTag: {
    fontSize: '0.75rem',
    color: colors.textSecondary,
    backgroundColor: colors.surfaceHover,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
  },
  favoriteSection: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  },
  feedbackSection: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  },
};

export default function LocalizadorDetalle() {
  const navigate = useNavigate();
  const { lugarId } = useParams();
  const [searchParams] = useSearchParams();
  const perfilId = searchParams.get('perfil');

  const [lugar, setLugar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function cargarLugar() {
      setLoading(true);
      setError(null);
      try {
        const data = await getPlace(lugarId);
        setLugar(data);
      } catch (err) {
        console.error('Error cargando lugar:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    cargarLugar();
  }, [lugarId]);

  const handleBack = () => {
    navigate(-1);
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.main}>
          <div style={styles.header}>
            <button style={styles.backButton} onClick={handleBack}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <h1 style={styles.title}>Cargando...</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <span style={{ fontSize: '2rem' }}>⏳</span>
            <p style={{ color: colors.textSecondary, marginTop: '1rem' }}>Cargando información...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (error || !lugar) {
    return (
      <div style={styles.container}>
        <div style={styles.main}>
          <div style={styles.header}>
            <button style={styles.backButton} onClick={handleBack}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <h1 style={styles.title}>No encontrado</h1>
          </div>
          <p>{error || 'El lugar que buscas no existe.'}</p>
        </div>
      </div>
    );
  }

  const esProveedor = lugar.tipo === 'independiente' || lugar.tipo === 'proveedor';

  // Calcular número de WhatsApp según el modelo de datos
  const whatsappNumber = lugar.tieneWhatsapp
    ? (lugar.whatsapp || lugar.telefono)
    : null;

  const renderEstado = () => {
    if (lugar.es24h) {
      return <span style={styles.estado24h}>Abierto 24 horas</span>;
    }
    if (lugar.estado === 'abierto') {
      return <span style={styles.estadoAbierto}>{lugar.estadoTexto}</span>;
    }
    if (lugar.estado === 'cerrado') {
      return (
        <span style={styles.estadoCerrado}>
          {lugar.estadoTexto} {lugar.horarioAbre && `· ${lugar.horarioAbre}`}
        </span>
      );
    }
    return null;
  };

  return (
    <div style={styles.container}>
      <div style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton} onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div>
            <h1 style={styles.title}>Volver</h1>
          </div>
        </div>

        {/* Foto */}
        <div style={styles.photoContainer}>
          {lugar.foto ? (
            <img src={lugar.foto} alt={lugar.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={styles.photoPlaceholder}>
              {esProveedor ? '👤' : '🏢'}
            </span>
          )}
        </div>

        {/* Nombre y rating */}
        <div style={styles.nombreRow}>
          <h2 style={styles.nombre}>{lugar.nombre}</h2>
          {lugar.source === 'google_cache' ? (
            <span style={{ ...styles.sourceBadge, ...styles.sourceGoogle }}>
              Google Maps
            </span>
          ) : (lugar.source === 'admin' || lugar.source === 'user_suggestion') && lugar.status === 'verificado' && (
            <span style={{ ...styles.sourceBadge, ...styles.sourceVerified }}>
              ✓ Verificado LemmonTags
            </span>
          )}
        </div>

        {lugar.rating && (
          <div style={styles.ratingContainer}>
            <span style={styles.star}>★</span>
            <span style={styles.rating}>{lugar.rating}</span>
            {(lugar.resenas || lugar.reviewCount) && (
              <span style={styles.resenas}>({lugar.resenas || lugar.reviewCount} reseñas)</span>
            )}
          </div>
        )}

        {(lugar.categoriaDisplay || lugar.categoria) && (
          <p style={styles.categoria}>{lugar.categoriaDisplay || lugar.categoria}</p>
        )}

        {/* Información de ubicación/horario */}
        <div style={styles.infoSection}>
          {/* Estado (para establecimientos) */}
          {!esProveedor && lugar.estado && (
            <div style={styles.estadoContainer}>
              {renderEstado()}
            </div>
          )}

          {/* Zonas (para proveedores) */}
          {esProveedor && lugar.zonas && (
            <>
              <p style={{ ...styles.detailLabel, marginBottom: '0.125rem' }}>Zona de servicio</p>
              <p style={styles.zonas}>
                {Array.isArray(lugar.zonas) ? lugar.zonas.join(', ') : lugar.zonas}
              </p>
            </>
          )}

          {/* Horario (para proveedores) */}
          {esProveedor && lugar.horario && (
            <>
              <p style={{ ...styles.detailLabel, marginBottom: '0.125rem', marginTop: '0.75rem' }}>Horario de atención</p>
              <p style={styles.horario}>{lugar.horario}</p>
            </>
          )}

          {/* Dirección (para establecimientos) */}
          {!esProveedor && lugar.direccion && (
            <>
              <p style={styles.direccion}>{lugar.direccion}</p>
              {lugar.ciudad && <p style={styles.ciudad}>{lugar.ciudad}</p>}
            </>
          )}
        </div>

        {/* Botones de acción */}
        <div style={styles.actionsSection}>
          <ActionButtons
            telefono={lugar.telefono}
            whatsapp={whatsappNumber}
            lat={lugar.lat}
            lng={lugar.lng}
            direccion={lugar.direccion}
          />
        </div>

        {/* Botón Agendar Cita (solo para veterinarias) */}
        {(lugar.categoria === 'veterinaria' || lugar.categoriaDisplay?.toLowerCase().includes('veterinar')) && (() => {
          // Detectar si es veterinaria registrada en LemmonTags
          const esRegistrada = (lugar.source === 'admin' || lugar.source === 'user_suggestion') && lugar.status === 'verificado';
          const lugarInfo = [lugar.nombre, lugar.direccion].filter(Boolean).join(' - ');

          return (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
              {esRegistrada && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.625rem 0.75rem', marginBottom: '0.75rem',
                  backgroundColor: '#d1fae5', borderRadius: '8px', fontSize: '0.8rem', color: '#059669'
                }}>
                  <span style={{ fontSize: '1rem' }}>✓</span>
                  <span>Esta veterinaria está registrada en LemmonTags</span>
                </div>
              )}
              <button
                onClick={() => {
                  if (esRegistrada && lugar.vetId) {
                    // Vet registrado con ID: ir directo al flujo de booking
                    navigate(`/userCalendarCitas?vetId=${lugar.vetId}`);
                  } else {
                    // No registrado o sin vetId: ir a recordatorio con lugar prellenado
                    navigate(`/userCalendarCitas?lugarNombre=${encodeURIComponent(lugarInfo)}`);
                  }
                }}
                style={{
                  width: '100%', padding: '0.875rem',
                  backgroundColor: colors.accent, color: '#fff',
                  border: 'none', borderRadius: '10px',
                  fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>{esRegistrada ? '✓' : '📅'}</span>
                {esRegistrada ? 'Agendar cita conectada' : 'Agendar cita aquí'}
              </button>
              {!esRegistrada && (
                <p style={{ textAlign: 'center', fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.5rem', marginBottom: 0 }}>
                  Se creará un recordatorio personal de tu cita
                </p>
              )}
            </div>
          );
        })()}

        {/* Información detallada */}
        <div style={styles.detailsSection}>
          <h3 style={styles.detailsTitle}>Información</h3>

          {lugar.metodosPago && lugar.metodosPago.length > 0 && (
            <div style={styles.detailRow}>
              <p style={styles.detailLabel}>Métodos de pago</p>
              <p style={styles.detailValue}>{lugar.metodosPago.join(', ')}</p>
            </div>
          )}

          {formatearEstacionamiento(lugar.estacionamiento) && (
            <div style={styles.detailRow}>
              <p style={styles.detailLabel}>Estacionamiento</p>
              <p style={styles.detailValue}>🅿️ {formatearEstacionamiento(lugar.estacionamiento)}</p>
            </div>
          )}

          {lugar.servicios && lugar.servicios.length > 0 && (
            <div style={styles.detailRow}>
              <p style={styles.detailLabel}>Servicios</p>
              <div style={styles.serviciosList}>
                {lugar.servicios.map((servicio, i) => (
                  <span key={i} style={styles.servicioTag}>{servicio}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Botón de favoritos */}
        <div style={styles.favoriteSection}>
          <FavoriteButton perfilId={perfilId} lugarId={lugar.id} />
        </div>

        {/* Feedback */}
        <div style={styles.feedbackSection}>
          <FeedbackButtons lugarNombre={lugar.nombre} tipoPersona={esProveedor} />
        </div>
      </div>
    </div>
  );
}
